// src/components/user/Masivo/DeliveryRowCard.jsx
import React from 'react';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Edit, 
  Trash2,
  Package,
  MapPin,
  Phone,
  User,
  DollarSign
} from 'lucide-react';

function DeliveryRowCard({ delivery, onEdit, onDelete }) {
  const { rowNumber, isValid, hasWarnings, errors, warnings, data } = delivery;
  const items = [1, 2, 3, 4, 5]
    .map(index => ({
      sku: data[`SKU ${index}`],
      quantity: data[`Cantidad ${index}`] || '1'
    }))
    .filter(item => item.sku);

  // Determine card style based on validation
  const getBorderColor = () => {
    if (!isValid) return 'border-red-300 bg-red-50/50';
    if (hasWarnings) return 'border-[#E6B43C]/30 bg-[#E6B43C]/5';
    return 'border-brand-green/30 bg-brand-green/5';
  };

  const getStatusIcon = () => {
    if (!isValid) return <XCircle className="w-5 h-5 text-red-600" />;
    if (hasWarnings) return <AlertTriangle className="w-5 h-5 text-[#E6B43C]" />;
    return <CheckCircle className="w-5 h-5 text-brand-green" />;
  };

  const getStatusText = () => {
    if (!isValid) return 'Error';
    if (hasWarnings) return 'Advertencia';
    return 'Válido';
  };

  const getStatusColor = () => {
    if (!isValid) return 'text-red-600';
    if (hasWarnings) return 'text-[#E6B43C]';
    return 'text-brand-green';
  };

  return (
    <div className={`border-2 rounded-xl p-4 transition-all hover:shadow-md ${getBorderColor()}`}>
      {/* Header with row number and status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="text-xs font-bold text-slate-500 font-montserrat">
            Fila {rowNumber}
          </span>
          <span className={`text-xs font-bold ${getStatusColor()} font-montserrat`}>
            {getStatusText()}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(delivery)}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            title="Editar"
          >
            <Edit className="w-4 h-4 text-slate-600" />
          </button>
          <button
            onClick={() => onDelete(delivery)}
            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
            title="Eliminar"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      </div>

      {/* Delivery details */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div className="flex items-start gap-2">
          <User className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs text-slate-500 font-montserrat">Cliente</p>
            <p className="text-sm font-semibold text-slate-900 font-montserrat truncate">
              {data['Nombre y Apellido'] || '-'}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <Phone className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs text-slate-500 font-montserrat">WhatsApp</p>
            <p className="text-sm font-semibold text-slate-900 font-montserrat">
              {data['WhatsApp'] || '-'}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs text-slate-500 font-montserrat">Distrito</p>
            <p className="text-sm font-semibold text-slate-900 font-montserrat">
              {data['Distrito'] || '-'}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <Package className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs text-slate-500 font-montserrat">Productos</p>
            <p className="text-sm font-semibold text-slate-900 font-montserrat truncate">
              {items.length > 0
                ? items.map(item => `${item.quantity}x ${item.sku}`).join(', ')
                : '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="bg-white/50 rounded-lg p-2 mb-3">
        <p className="text-xs text-slate-500 font-montserrat mb-1">Dirección</p>
        <p className="text-sm text-slate-700 font-montserrat">
          {data['Direccion'] || '-'}
        </p>
      </div>

      {/* Optional fields */}
      <div className="flex flex-wrap gap-2 mb-3">
        {items.map((item, index) => (
          <span key={`${item.sku}-${index}`} className="px-2 py-1 bg-slate-100 rounded text-xs font-montserrat text-slate-700">
            SKU {index + 1}: {item.sku} ({item.quantity})
          </span>
        ))}
        {data['Cobrar'] && parseFloat(data['Cobrar']) > 0 && (
          <span className="px-2 py-1 bg-brand-green/10 rounded text-xs font-montserrat text-brand-green font-semibold flex items-center gap-1">
            <DollarSign className="w-3 h-3" />
            S/ {data['Cobrar']}
          </span>
        )}
        {data['Latitud'] && data['Longitud'] && (
          <span className="px-2 py-1 bg-brand-primary/10 rounded text-xs font-montserrat text-brand-primary font-semibold flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            GPS {data['Latitud']}, {data['Longitud']}
          </span>
        )}
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="space-y-1">
          {errors.map((error, idx) => (
            <div key={idx} className="flex items-start gap-2 bg-red-50 rounded-lg p-2">
              <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700 font-montserrat">
                {error.message}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="space-y-1">
          {warnings.map((warning, idx) => (
            <div key={idx} className="flex items-start gap-2 bg-[#E6B43C]/10 rounded-lg p-2">
              <AlertTriangle className="w-4 h-4 text-[#E6B43C] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-slate-700 font-montserrat">
                  {warning.message}
                </p>
                {warning.suggestion && (
                  <p className="text-xs text-brand-primary font-montserrat mt-1">
                    Sugerencia: <strong>{warning.suggestion}</strong>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DeliveryRowCard;
